import { useEffect } from 'react';
import { isOwnerSite, OWNER_SITE_URL } from '../config/siteMode';

/**
 * Injects the PetTrek LocalBusiness / ProfessionalService JSON-LD block into
 * <head> while the owner landing page is mounted.
 *
 * Lives here (not statically in public/index.html) because index.html is
 * shared by BOTH builds, and the handler site has no marketing landing to
 * make LocalBusiness claims about. Google is the only crawler that consumes
 * JSON-LD and it renders JavaScript, so a runtime injection loses nothing.
 */

const SITE_URL = OWNER_SITE_URL.replace(/\/+$/, '');
const SCRIPT_ID = 'pettrek-local-business-jsonld';

export const LOCAL_BUSINESS_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': ['LocalBusiness', 'ProfessionalService'],
  name: 'PetTrek',
  description:
    'On-demand dog walking, pet sitting, grooming and training from verified handlers across South Africa.',
  url: `${SITE_URL}/`,
  image: `${SITE_URL}/og-image.png`,
  logo: `${SITE_URL}/logo512.png`,
  areaServed: { '@type': 'Country', name: 'South Africa' },
  priceRange: 'R100+',
  currenciesAccepted: 'ZAR',
  hasOfferCatalog: {
    '@type': 'OfferCatalog',
    name: 'Pet care services',
    itemListElement: [
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Dog walking' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Pet sitting' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Pet grooming' } },
      { '@type': 'Offer', itemOffered: { '@type': 'Service', name: 'Dog training' } },
    ],
  },
};

export default function LocalBusinessJsonLd() {
  useEffect(() => {
    // The handler site has no marketing landing, so it makes no business claims.
    if (!isOwnerSite) return undefined;
    // Guard against double-mounting (e.g. React 18 StrictMode remounts).
    if (document.getElementById(SCRIPT_ID)) return undefined;

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = SCRIPT_ID;
    script.text = JSON.stringify(LOCAL_BUSINESS_SCHEMA);
    document.head.appendChild(script);

    return () => {
      script.remove();
    };
  }, []);

  return null;
}
